import { type ReactNode, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { type TaskView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { Dialog } from '../../../components/ui/dialog';
import { errorMessage } from '../../../lib/error-message';
import { apiFetch } from '../../../lib/http';
import { playLeave } from '../card-motion';
import { invalidateProjectTasks } from '../invalidate-project-tasks';

/**
 * Nút "Xoá việc" ở chi tiết việc — APM-TDEL-b.
 *
 * Chỉ hiện khi `task.canDelete` (PM của dự án hoặc người tạo việc, APM-TDEL-a).
 * Hỏi lại trước khi xoá, và câu hỏi nói hai điều người bấm cần biết: việc con
 * đi theo, và việc **lấy lại được** ở tab Thay đổi — không nói điều thứ hai thì
 * người ta ngần ngại xoá cả những việc đáng xoá.
 */
export function DeleteTaskButton({
  task,
  onDeleted,
}: {
  readonly task: TaskView;
  readonly onDeleted: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);

  const remove = useMutation({
    mutationFn: () => apiFetch<void>(`/tasks/${task.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success(t('taskDelete.deleted', { key: task.key }));
      setAsking(false);
      onDeleted();
      // Thẻ trên board thu nhỏ, mờ rồi co lại **trước**, dữ liệu làm mới sau —
      // APM-BOARD-fx. Không có thẻ nào đang hiện thì làm mới ngay.
      playLeave(task.id, () => invalidateProjectTasks(queryClient, task.projectId));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!task.canDelete) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setAsking(true)}
        aria-label={t('taskDelete.button')}
        title={t('taskDelete.button')}
        className="glass-hover rounded-full p-1 text-muted-foreground hover:text-destructive"
      >
        <Trash2 className="h-4 w-4" aria-hidden />
      </button>

      {asking ? (
        <Dialog label={t('taskDelete.title', { key: task.key })} onClose={() => setAsking(false)}>
          <div className="flex flex-col gap-3">
            <h2 className="text-sm font-medium">{t('taskDelete.title', { key: task.key })}</h2>
            <p className="text-sm">{task.title}</p>
            <p className="text-sm text-muted-foreground">{t('taskDelete.body')}</p>
            {task.subtaskCount > 0 ? (
              <p className="text-sm text-rag-amber">
                {t('taskDelete.withChildren', { count: task.subtaskCount })}
              </p>
            ) : null}
            <div className="flex items-center gap-2">
              <Button
                variant="destructive"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                {t('taskDelete.confirm')}
              </Button>
              <Button variant="ghost" onClick={() => setAsking(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
